import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../i18n/LanguageContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ChevronLeft, ChevronRight, Calendar as CalendarIcon, 
  UserCheck, Clock, MapPin, Loader2, DollarSign,
  AlertCircle, Check, X
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Calendar Day Cell
const CalendarDay = ({ 
  date, 
  isCurrentMonth, 
  isToday, 
  assignments, 
  availability,
  onClick 
}) => {
  const dayAssignments = assignments.filter(a => {
    const assignDate = new Date(a.game_date || a.created_at);
    return assignDate.toDateString() === date.toDateString();
  });

  const isAvailable = availability?.available_days?.includes(date.getDay());
  const hasAssignments = dayAssignments.length > 0;

  return (
    <div
      onClick={() => onClick(date, dayAssignments)}
      className={`
        min-h-[100px] p-2 border-r border-b cursor-pointer transition-colors
        ${!isCurrentMonth ? 'bg-muted/30 text-muted-foreground' : 'hover:bg-muted/50'}
        ${isToday ? 'bg-primary/10 border-primary' : ''}
      `}
    >
      <div className="flex items-center justify-between mb-1">
        <span className={`
          text-sm font-medium w-7 h-7 flex items-center justify-center rounded-full
          ${isToday ? 'bg-primary text-primary-foreground' : ''}
        `}>
          {date.getDate()}
        </span>
        {isCurrentMonth && (
          <div className="flex gap-1">
            {isAvailable && !hasAssignments && (
              <div className="w-2 h-2 rounded-full bg-green-500" title="Available" />
            )}
            {hasAssignments && (
              <div className="w-2 h-2 rounded-full bg-blue-500" title="Has assignments" />
            )}
          </div>
        )}
      </div>
      
      {isCurrentMonth && (
        <div className="space-y-1">
          {dayAssignments.slice(0, 2).map((assignment, idx) => (
            <div
              key={assignment.id || idx}
              className={`
                text-xs p-1 rounded truncate
                ${assignment.status === 'completed' ? 'bg-green-500/20 text-green-700' : 'bg-blue-500/20 text-blue-700'}
              `}
            >
              {assignment.game_name || 'Game'}
            </div>
          ))}
          {dayAssignments.length > 2 && (
            <div className="text-xs text-muted-foreground">
              +{dayAssignments.length - 2} more
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Day Detail Dialog
const DayDetailDialog = ({ 
  isOpen, 
  onClose, 
  date, 
  assignments,
  onUpdateStatus 
}) => {
  if (!date) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarIcon className="w-5 h-5" />
            {date.toLocaleDateString('en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-3 max-h-[400px] overflow-y-auto">
          {assignments.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <CalendarIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No assignments for this day</p>
            </div>
          ) : (
            assignments.map((assignment) => (
              <Card key={assignment.id} className="overflow-hidden">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-semibold">{assignment.game_name || 'Game Assignment'}</h4>
                      <div className="flex flex-wrap gap-2 mt-2 text-sm text-muted-foreground">
                        {assignment.scheduled_time && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {new Date(assignment.scheduled_time).toLocaleTimeString([], { 
                              hour: '2-digit', 
                              minute: '2-digit' 
                            })}
                          </span>
                        )}
                        {assignment.field && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {assignment.field}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <DollarSign className="w-3 h-3" />
                          ${assignment.pay_rate || 25}
                        </span>
                      </div>
                    </div>
                    <Badge variant={
                      assignment.status === 'completed' ? 'default' :
                      assignment.status === 'assigned' ? 'secondary' : 'outline'
                    }>
                      {assignment.status}
                    </Badge>
                  </div>
                  
                  {assignment.status === 'assigned' && (
                    <div className="flex gap-2 mt-3 pt-3 border-t">
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 text-green-600 hover:bg-green-50"
                        onClick={() => onUpdateStatus(assignment.id, 'confirmed')}
                      >
                        <Check className="w-4 h-4 mr-1" />
                        Confirm
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 text-red-600 hover:bg-red-50"
                        onClick={() => onUpdateStatus(assignment.id, 'declined')}
                      >
                        <X className="w-4 h-4 mr-1" />
                        Decline
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const RefereeCalendar = () => {
  const { token, user } = useAuth();
  const { t } = useLanguage();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [assignments, setAssignments] = useState([]);
  const [availability, setAvailability] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedAssignments, setSelectedAssignments] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);

  const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  useEffect(() => {
    fetchData();
  }, [currentDate, token]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      
      // Get month range
      const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      
      const [assignmentsRes, availabilityRes] = await Promise.all([
        axios.get(`${API}/referee/assignments`, { headers }),
        axios.get(`${API}/referee/profile`, { headers }).catch(() => ({ data: null }))
      ]);
      
      // Enrich assignments with game data
      const enrichedAssignments = await Promise.all(
        assignmentsRes.data.map(async (assignment) => {
          try {
            const gameRes = await axios.get(`${API}/games/${assignment.game_id}`, { headers });
            return {
              ...assignment,
              game_name: `${gameRes.data.team1_name} vs ${gameRes.data.team2_name}`,
              scheduled_time: gameRes.data.scheduled_time,
              field: gameRes.data.field,
              game_date: gameRes.data.scheduled_time
            };
          } catch (e) {
            return {
              ...assignment,
              game_name: 'Game',
              game_date: assignment.created_at
            };
          }
        })
      );
      
      setAssignments(enrichedAssignments);
      setAvailability(availabilityRes.data);
    } catch (error) {
      console.error('Failed to fetch calendar data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    const days = [];
    
    // Add days from previous month
    const firstDayOfWeek = firstDay.getDay();
    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      const date = new Date(year, month, -i);
      days.push({ date, isCurrentMonth: false });
    }
    
    // Add days of current month
    for (let i = 1; i <= lastDay.getDate(); i++) {
      const date = new Date(year, month, i);
      days.push({ date, isCurrentMonth: true });
    }
    
    // Add days from next month to complete the grid
    const remainingDays = 42 - days.length; // 6 rows * 7 days
    for (let i = 1; i <= remainingDays; i++) {
      const date = new Date(year, month + 1, i);
      days.push({ date, isCurrentMonth: false });
    }
    
    return days;
  };

  const navigateMonth = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + direction);
      return newDate;
    });
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const handleDayClick = (date, dayAssignments) => {
    setSelectedDate(date);
    setSelectedAssignments(dayAssignments);
    setDialogOpen(true);
  };

  const handleUpdateStatus = async (assignmentId, newStatus) => {
    try {
      await axios.patch(
        `${API}/referee/assignments/${assignmentId}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Assignment ${newStatus}`);
      fetchData();
      setDialogOpen(false);
    } catch (error) {
      toast.error('Failed to update assignment');
    }
  };

  const today = new Date();
  const calendarDays = getCalendarDays();

  // Calculate stats
  const thisMonthAssignments = assignments.filter(a => {
    const date = new Date(a.game_date || a.created_at);
    return date.getMonth() === currentDate.getMonth() && 
           date.getFullYear() === currentDate.getFullYear();
  });
  const totalEarnings = thisMonthAssignments.reduce((sum, a) => sum + (a.pay_rate || 25), 0);
  const completedGames = thisMonthAssignments.filter(a => a.status === 'completed').length;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <CalendarIcon className="w-6 h-6" />
              {t('game.referee')} {t('nav.schedule') || 'Schedule'}
            </h1>
            <p className="text-muted-foreground">
              View your game assignments and availability
            </p>
          </div>
          
          <Link to="/referee">
            <Button variant="outline" className="gap-2">
              <UserCheck className="w-4 h-4" />
              Referee Dashboard
            </Button>
          </Link>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <CalendarIcon className="w-6 h-6 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{thisMonthAssignments.length}</p>
                <p className="text-sm text-muted-foreground">Games This Month</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-green-500/10 flex items-center justify-center">
                <Check className="w-6 h-6 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{completedGames}</p>
                <p className="text-sm text-muted-foreground">Completed</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-yellow-500/10 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-yellow-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">${totalEarnings}</p>
                <p className="text-sm text-muted-foreground">Potential Earnings</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Calendar */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl">
                {MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={goToToday}>
                  Today
                </Button>
                <Button variant="ghost" size="icon" onClick={() => navigateMonth(-1)}>
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => navigateMonth(1)}>
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </div>
            </div>
          </CardHeader>
          
          <CardContent className="p-0">
            {/* Day Headers */}
            <div className="grid grid-cols-7 border-b">
              {DAYS_OF_WEEK.map(day => (
                <div 
                  key={day} 
                  className="p-3 text-center text-sm font-medium text-muted-foreground border-r last:border-r-0"
                >
                  {day}
                </div>
              ))}
            </div>
            
            {/* Calendar Grid */}
            <div className="grid grid-cols-7">
              {calendarDays.map(({ date, isCurrentMonth }, idx) => (
                <CalendarDay
                  key={idx}
                  date={date}
                  isCurrentMonth={isCurrentMonth}
                  isToday={date.toDateString() === today.toDateString()}
                  assignments={assignments}
                  availability={availability}
                  onClick={handleDayClick}
                />
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Legend */}
        <div className="flex items-center gap-6 mt-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-green-500" />
            <span>Available</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-blue-500" />
            <span>Has Assignments</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-primary" />
            <span>Today</span>
          </div>
        </div>

        {/* Day Detail Dialog */}
        <DayDetailDialog
          isOpen={dialogOpen}
          onClose={() => setDialogOpen(false)}
          date={selectedDate}
          assignments={selectedAssignments}
          onUpdateStatus={handleUpdateStatus}
        />
      </div>
    </div>
  );
};

export default RefereeCalendar;
